import { createSlice, combineReducers } from '@reduxjs/toolkit';
import 'react';
import { onOnline, onOffline, onFocus, onFocusLost } from './setupListeners.js';
import { QueryStatus } from './apiState.js';
import { isOnline } from '../utils/isOnline.js';
import { isDocumentVisible } from '../utils/isDocumentVisible.js';
import { copyWithStructuralSharing } from '../utils/copyWithStructuralSharing.js';
import { calculateProvidedBy } from '../endpointDefinitions.js';
import { applyPatches } from 'immer';

function updateQuerySubstateIfExists(state, queryCacheKey, update) {
  const substate = state[queryCacheKey];

  if (substate) {
    update(substate);
  }
}

function updateMutationSubstateIfExists(state, {
  requestId
}, update) {
  const substate = state[requestId];

  if (substate) {
    update(substate);
  }
}

function buildSlice({
  reducerPath,
  queryThunk,
  mutationThunk,
  context: {
    endpointDefinitions: definitions
  },
  assertEntityType,
  config
}) {
  const querySlice = createSlice({
    name: `${reducerPath}/queries`,
    initialState: {},
    reducers: {
      removeQueryResult(draft, {
        payload: {
          queryCacheKey
        }
      }) {
        delete draft[queryCacheKey];
      },

      queryResultPatched(draft, {
        payload: {
          queryCacheKey,
          patches
        }
      }) {
        updateQuerySubstateIfExists(draft, queryCacheKey, substate => {
          substate.data = applyPatches(substate.data, patches);
        });
      }

    },

    extraReducers(builder) {
      builder.addCase(queryThunk.pending, (draft, {
        meta: {
          arg,
          requestId
        }
      }) => {
        if (arg.subscribe) {
          var _arg$queryCacheKey, _draft$_arg$queryCach;

          // only initialize substate if we want to subscribe to it
          (_draft$_arg$queryCach = draft[_arg$queryCacheKey = arg.queryCacheKey]) != null ? _draft$_arg$queryCach : draft[_arg$queryCacheKey] = {
            status: QueryStatus.uninitialized,
            endpoint: arg.endpoint
          };
        }

        updateQuerySubstateIfExists(draft, arg.queryCacheKey, substate => {
          substate.status = QueryStatus.pending;
          substate.requestId = requestId;
          substate.internalQueryArgs = arg.internalQueryArgs;
          substate.originalArgs = arg.originalArgs;
          substate.startedTimeStamp = arg.startedTimeStamp;
        });
      }).addCase(queryThunk.fulfilled, (draft, {
        meta,
        payload
      }) => {
        updateQuerySubstateIfExists(draft, meta.arg.queryCacheKey, substate => {
          if (substate.requestId !== meta.requestId) return;
          substate.status = QueryStatus.fulfilled;
          substate.data = copyWithStructuralSharing(substate.data, payload.result);
          substate.error = undefined;
          substate.fulfilledTimeStamp = payload.fulfilledTimeStamp;
        });
      }).addCase(queryThunk.rejected, (draft, {
        meta: {
          condition,
          arg,
          requestId
        },
        error,
        payload
      }) => {
        updateQuerySubstateIfExists(draft, arg.queryCacheKey, substate => {
          if (condition) ; else {
            // request failed
            if (substate.requestId !== requestId) return;
            substate.status = QueryStatus.rejected;
            substate.error = payload != null ? payload : error;
          }
        });
      });
    }

  });
  const mutationSlice = createSlice({
    name: `${reducerPath}/mutations`,
    initialState: {},
    reducers: {
      unsubscribeResult(draft, action) {
        if (action.payload.requestId in draft) {
          delete draft[action.payload.requestId];
        }
      }

    },

    extraReducers(builder) {
      builder.addCase(mutationThunk.pending, (draft, {
        meta: {
          arg,
          requestId
        }
      }) => {
        if (!arg.track) return;
        draft[requestId] = {
          status: QueryStatus.pending,
          internalQueryArgs: arg.internalQueryArgs,
          originalArgs: arg.originalArgs,
          endpoint: arg.endpoint,
          startedTimeStamp: arg.startedTimeStamp
        };
      }).addCase(mutationThunk.fulfilled, (draft, {
        payload,
        meta: {
          requestId,
          arg
        }
      }) => {
        if (!arg.track) return;
        updateMutationSubstateIfExists(draft, {
          requestId
        }, substate => {
          substate.status = QueryStatus.fulfilled;
          substate.data = payload.result;
          substate.fulfilledTimeStamp = payload.fulfilledTimeStamp;
        });
      }).addCase(mutationThunk.rejected, (draft, {
        payload,
        error,
        meta: {
          requestId,
          arg
        }
      }) => {
        if (!arg.track) return;
        updateMutationSubstateIfExists(draft, {
          requestId
        }, substate => {
          substate.status = QueryStatus.rejected;
          substate.error = payload != null ? payload : error;
        });
      });
    }

  });
  const invalidationSlice = createSlice({
    name: `${reducerPath}/invalidation`,
    initialState: {},
    reducers: {},

    extraReducers(builder) {
      builder.addCase(queryThunk.fulfilled, (draft, {
        payload,
        meta: {
          arg
        }
      }) => {
        const {
          endpoint,
          queryCacheKey
        } = arg;
        const providedEntities = calculateProvidedBy(definitions[endpoint].provides, payload.result, arg.originalArgs, assertEntityType);

        for (const {
          type,
          id
        } of providedEntities) {
          var _draft$type, _ref, _draft$type$_ref, _draft$type2;

          const subscribedQueries = (_draft$type$_ref = (_draft$type = (_draft$type2 = draft[type]) != null ? _draft$type2 : draft[type] = {})[_ref = id || '__internal_without_id']) != null ? _draft$type$_ref : _draft$type[_ref] = [];
          const alreadySubscribed = subscribedQueries.includes(queryCacheKey);

          if (!alreadySubscribed) {
            subscribedQueries.push(queryCacheKey);
          }
        }
      }).addCase(querySlice.actions.removeQueryResult, (draft, {
        payload: {
          queryCacheKey
        }
      }) => {
        for (const entityTypeSubscriptions of Object.values(draft)) {
          for (const idSubscriptions of Object.values(entityTypeSubscriptions)) {
            const foundAt = idSubscriptions.indexOf(queryCacheKey);

            if (foundAt !== -1) {
              idSubscriptions.splice(foundAt, 1);
            }
          }
        }
      });
    }

  });
  const subscriptionSlice = createSlice({
    name: `${reducerPath}/subscriptions`,
    initialState: {},
    reducers: {
      updateSubscriptionOptions(draft, {
        payload: {
          queryCacheKey,
          requestId,
          options
        }
      }) {
        var _draft$queryCacheKey;

        if (draft == null ? void 0 : (_draft$queryCacheKey = draft[queryCacheKey]) == null ? void 0 : _draft$queryCacheKey[requestId]) {
          draft[queryCacheKey][requestId] = options;
        }
      },

      unsubscribeResult(draft, {
        payload: {
          queryCacheKey,
          requestId
        }
      }) {
        if (draft[queryCacheKey]) {
          delete draft[queryCacheKey][requestId];
        }
      }

    },
    extraReducers: builder => {
      builder.addCase(querySlice.actions.removeQueryResult, (draft, {
        payload: {
          queryCacheKey
        }
      }) => {
        delete draft[queryCacheKey];
      }).addCase(queryThunk.pending, (draft, {
        meta: {
          arg,
          requestId
        }
      }) => {
        if (arg.subscribe) {
          var _arg$queryCacheKey2, _draft$_arg$queryCach2, _ref2, _arg$subscriptionOpti;

          const substate = (_draft$_arg$queryCach2 = draft[_arg$queryCacheKey2 = arg.queryCacheKey]) != null ? _draft$_arg$queryCach2 : draft[_arg$queryCacheKey2] = {};
          substate[requestId] = (_ref2 = (_arg$subscriptionOpti = arg.subscriptionOptions) != null ? _arg$subscriptionOpti : substate[requestId]) != null ? _ref2 : {};
        }
      }).addCase(queryThunk.rejected, (draft, {
        meta: {
          condition,
          arg,
          requestId
        },
        error,
        payload
      }) => {
        const substate = draft[arg.queryCacheKey]; // request was aborted due to condition (another query already running)

        if (condition && arg.subscribe && substate) {
          var _ref3, _arg$subscriptionOpti2;

          substate[requestId] = (_ref3 = (_arg$subscriptionOpti2 = arg.subscriptionOptions) != null ? _arg$subscriptionOpti2 : substate[requestId]) != null ? _ref3 : {};
        }
      });
    }
  });
  const configSlice = createSlice({
    name: `${reducerPath}/config`,
    initialState: {
      online: isOnline(),
      focused: isDocumentVisible(),
      ...config
    },
    reducers: {},
    extraReducers: builder => {
      builder.addCase(onOnline, state => {
        state.online = true;
      }).addCase(onOffline, state => {
        state.online = false;
      }).addCase(onFocus, state => {
        state.focused = true;
      }).addCase(onFocusLost, state => {
        state.focused = false;
      });
    }
  });
  const reducer = combineReducers({
    queries: querySlice.reducer,
    mutations: mutationSlice.reducer,
    provided: invalidationSlice.reducer,
    subscriptions: subscriptionSlice.reducer,
    config: configSlice.reducer
  });
  const actions = {
    updateSubscriptionOptions: subscriptionSlice.actions.updateSubscriptionOptions,
    queryResultPatched: querySlice.actions.queryResultPatched,
    removeQueryResult: querySlice.actions.removeQueryResult,
    unsubscribeQueryResult: subscriptionSlice.actions.unsubscribeResult,
    unsubscribeMutationResult: mutationSlice.actions.unsubscribeResult
  };
  return {
    reducer,
    actions
  };
}

export { buildSlice };
//# sourceMappingURL=buildSlice.js.map
